import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { usePageTitle } from '../hooks/usePageTitle';
import './CaseStudies.css';

function CaseStudySkeleton() {
  return (
    <div className="case-study skeleton-case" aria-hidden="true">
      <div className="case-header">
        <div className="cs-skel" style={{ width: '45%', height: 22 }} />
        <div className="cs-skel" style={{ width: 90, height: 26, borderRadius: 20 }} />
      </div>
      <div className="case-content">
        <div className="case-problem">
          <div className="cs-skel" style={{ width: 80, height: 16, marginBottom: '1rem' }} />
          <div className="cs-skel" style={{ width: '100%', height: 13, marginBottom: '0.5rem' }} />
          <div className="cs-skel" style={{ width: '85%', height: 13 }} />
        </div>
        <div className="case-solution">
          <div className="cs-skel" style={{ width: 80, height: 16, marginBottom: '1rem' }} />
          <div className="cs-skel" style={{ width: '100%', height: 13, marginBottom: '0.5rem' }} />
          <div className="cs-skel" style={{ width: '90%', height: 13 }} />
        </div>
        <div className="case-results">
          <div className="cs-skel" style={{ width: 80, height: 16, marginBottom: '1rem' }} />
          <div className="results-grid">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="result-item">
                <div className="cs-skel" style={{ width: 60, height: 28, margin: '0 auto 0.5rem' }} />
                <div className="cs-skel" style={{ width: '80%', height: 13, margin: '0 auto' }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CaseStudies() {
  usePageTitle('Case Studies');
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('case_studies').select('*').order('created_at', { ascending: false }).then(({ data }) => {
      if (data && data.length > 0) {
        setCaseStudies(data);
      } else {
        // Default fallback
      const defaultCases = [
        {
          id: 1,
          title: 'Manufacturing Defect Detection System',
          tag: 'Computer Vision',
          problem: 'A mid-sized electronics manufacturer was experiencing 12% defect rates in their production line. Manual inspection was slow, inconsistent, and costly with 8 quality inspectors working full-time.',
          solution: 'We deployed an AI-powered computer vision system using custom-trained models to detect defects in real-time. The system integrated with existing production cameras and provided instant alerts.',
          technology: 'Python, TensorFlow, OpenCV, AWS, React dashboard',
          results: [
            { number: '95%', label: 'Defect detection accuracy' },
            { number: '$480K', label: 'Annual cost savings' },
            { number: '75%', label: 'Reduction in inspection time' },
            { number: '3 months', label: 'ROI achieved' }
          ]
        },
        {
          id: 2,
          title: 'Healthcare AI Assistant for Patient Support',
          tag: 'Conversational AI',
          problem: 'A healthcare network with 15 clinics was overwhelmed with patient inquiries. Average wait time was 18 minutes, and 40% of calls were for simple appointment scheduling or basic questions.',
          solution: 'We built an AI-powered voice and chat assistant that handles appointment scheduling, answers common questions, and routes complex queries to human staff. Integrated with their EHR system.',
          technology: 'GPT-4, RAG, Twilio, React, Node.js, HIPAA-compliant infrastructure',
          results: [
            { number: '82%', label: 'Queries handled autonomously' },
            { number: '2 min', label: 'Average response time' },
            { number: '$320K', label: 'Annual savings' },
            { number: '94%', label: 'Patient satisfaction' }
          ]
        },
        {
          id: 3,
          title: 'Sales Automation Platform for B2B SaaS',
          tag: 'AI Automation',
          problem: 'A B2B SaaS startup was spending 60% of their sales team\'s time on lead qualification and follow-ups. Only 15% of leads were qualified, wasting significant resources.',
          solution: 'We developed an agentic AI system that automatically qualifies leads, sends personalized outreach, schedules meetings, and updates CRM. The AI learns from successful conversions.',
          technology: 'LangChain, GPT-4, Python, Salesforce API, email automation',
          results: [
            { number: '3x', label: 'Increase in qualified leads' },
            { number: '65%', label: 'Time saved per rep' },
            { number: '$1.2M', label: 'Additional revenue (6 months)' },
            { number: '45 days', label: 'Development time' }
          ]
        },
        {
          id: 4,
          title: 'Fitness Membership Engagement Platform',
          tag: 'Mobile App + AI',
          problem: 'A fitness chain with 25 locations had 35% annual member churn. Generic communication and lack of personalization were driving members away.',
          solution: 'We built a mobile app with AI-driven personalization, workout recommendations, progress tracking, and automated engagement campaigns based on member behavior.',
          technology: 'React Native, Python ML models, Firebase, push notifications',
          results: [
            { number: '45%', label: 'Increase in retention' },
            { number: '78%', label: 'App adoption rate' },
            { number: '$890K', label: 'Additional annual revenue' },
            { number: '4.8/5', label: 'App store rating' }
          ]
        }
      ];
        setCaseStudies(defaultCases);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div className="case-studies">
      <section className="case-hero">
        <div className="case-container">
          <h1 className="case-hero-title">Case Studies</h1>
          <p className="case-hero-subtitle">Real results from real projects</p>
        </div>
      </section>

      <section className="case-main">
        <div className="case-container">
          {loading ? (
            Array.from({ length: 2 }).map((_, i) => <CaseStudySkeleton key={i} />)
          ) : caseStudies.map(cs => (
            <div key={cs.id} className="case-study">
              <div className="case-header">
                <h2>{cs.title}</h2>
                <span className="case-tag">{cs.tag}</span>
              </div>
              <div className="case-content">
                <div className="case-problem">
                  <h3>Problem</h3>
                  <p>{cs.problem}</p>
                </div>
                <div className="case-solution">
                  <h3>Solution</h3>
                  <p>{cs.solution}</p>
                  <p><strong>Technology:</strong> {cs.technology}</p>
                </div>
                <div className="case-results">
                  <h3>Results</h3>
                  <div className="results-grid">
                    {cs.results.map((result, idx) => (
                      <div key={idx} className="result-item">
                        <div className="result-number">{result.number}</div>
                        <div className="result-label">{result.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export default CaseStudies;

